/**
 * @voltxampere/web — `<vxa-pane-router>` (Sprint 18 Paket B B2 → B7).
 *
 * Mockup zenit-platform.html'deki 5 sekme yapısının ayraç'ı.
 * `currentPane` signal'i hangi pane'in görüntüleneceğini belirler.
 * Sprint 18 Paket B sonunda 5 pane'in 5'i de gerçek component'e
 * bağlı: vxa-layout (simulator), vxa-pane-landing/presets/inspector/
 * settings.
 *
 * Pane swap'inde fadeIn animation (mockup'tan birebir 400ms ease)
 * kullanıcıya geçişin algılanmasını sağlar. `prefers-reduced-motion`
 * altında animation iptal — engellilere saygı.
 *
 * URL hash sync `attachRouterHashSync` ile aktif olur; bu component
 * mount'ta sync'i bağlar, unmount'ta dispose eder. Tek instance
 * dışında bir sync olmaması için bağlama idempotent.
 *
 * Copyright © 2026 360 Studio
 * Proprietary — All rights reserved.
 */

import { LitElement, html, css } from "lit";
import { customElement } from "lit/decorators.js";

import "./vxa-layout.js";
import "./vxa-pane-landing.js";
import "./vxa-pane-presets.js";
import "./vxa-pane-inspector.js";
import "./vxa-pane-settings.js";

import { currentPane, attachRouterHashSync } from "../store/router.js";
import { SignalController } from "../lit-utils/signal-controller.js";

@customElement("vxa-pane-router")
export class VxaPaneRouter extends LitElement {
  private readonly _pane = new SignalController(this, currentPane);
  private _disposeHashSync: (() => void) | null = null;

  static override styles = css`
    :host {
      display: block;
      height: 100%;
      min-height: 0;
      overflow: hidden;
      position: relative;
    }

    .pane {
      height: 100%;
      min-height: 0;
      animation: fadeIn 400ms ease;
    }

    @media (prefers-reduced-motion: reduce) {
      .pane {
        animation: none;
      }
    }

    @keyframes fadeIn {
      from {
        opacity: 0;
        transform: translateY(8px);
      }
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }
  `;

  override connectedCallback(): void {
    super.connectedCallback();
    this._disposeHashSync ??= attachRouterHashSync();
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    this._disposeHashSync?.();
    this._disposeHashSync = null;
  }

  override render() {
    const pane = this._pane.value;
    switch (pane) {
      case "simulator":
        return html`<div class="pane" data-pane="simulator">
          <vxa-layout></vxa-layout>
        </div>`;
      case "landing":
        return html`<div class="pane" data-pane="landing">
          <vxa-pane-landing></vxa-pane-landing>
        </div>`;
      case "presets":
        return html`<div class="pane" data-pane="presets">
          <vxa-pane-presets></vxa-pane-presets>
        </div>`;
      case "inspector":
        return html`<div class="pane" data-pane="inspector">
          <vxa-pane-inspector></vxa-pane-inspector>
        </div>`;
      case "settings":
        return html`<div class="pane" data-pane="settings">
          <vxa-pane-settings></vxa-pane-settings>
        </div>`;
    }
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "vxa-pane-router": VxaPaneRouter;
  }
}
